import React, { useState, useEffect, useCallback, useRef } from 'react';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Badge } from '../components/ui/badge';
import { ScrollArea } from '../components/ui/scroll-area';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from '../components/ui/dialog';
import DrawerShell from '../components/DrawerShell';
import { Label } from '../components/ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { Tooltip, TooltipContent, TooltipTrigger, TooltipProvider } from '../components/ui/tooltip';
import { toast } from 'sonner';
import {
  ShoppingCart, Package, Search, Plus, Trash2, FileText, Truck, Check, X,
  TrendingUp, Clock, CheckCircle, Eye, Building2, RefreshCw, ArrowLeft,
  Printer, Info, Box, Activity, AlertTriangle, Zap, ArrowUpRight, Flame, ShieldAlert, Layers,
  LogOut,
} from 'lucide-react';
import axios from 'axios';
import { useAuth } from '../contexts/AuthContext';
import { formatDateIST as formatDate } from '../utils/date';

const API_URL = process.env.REACT_APP_BACKEND_URL;
const HOSPITAL = {
  name: 'Porunai Hospitals Pvt Ltd', address: 'No. 1/120, Bypass Road, Reddiarpatti, Tenkasi',
  city: 'Tenkasi, Tamil Nadu - 627803', gstin: '33AABCP1234H1Z5',
  phone: '+91 4633 260 260', email: 'purchase@porunaihospitals.com',
};

function formatCurrency(v) { return new Intl.NumberFormat('en-IN', { style: 'currency', currency: 'INR', maximumFractionDigits: 2 }).format(v || 0); }

const SC = { pending: 'bg-amber-100 text-amber-700', ordered: 'bg-blue-100 text-blue-700', received: 'bg-green-100 text-green-700', cancelled: 'bg-gray-100 text-gray-500', draft: 'bg-gray-100 text-gray-600', sent: 'bg-blue-100 text-blue-700', partially_received: 'bg-amber-100 text-amber-700', closed: 'bg-green-100 text-green-700' };

function getUrgency(rxCount, stock) {
  if (stock === 0 || stock === 'Nil') return { level: 'critical', label: 'Critical', color: 'text-red-700 bg-red-50 border-red-200', icon: ShieldAlert };
  if (stock !== null && stock !== undefined && stock <= 5) return { level: 'high', label: 'Urgent', color: 'text-orange-700 bg-orange-50 border-orange-200', icon: Flame };
  if (rxCount >= 3 && stock <= 20) return { level: 'medium', label: 'Reorder', color: 'text-amber-700 bg-amber-50 border-amber-200', icon: AlertTriangle };
  return { level: 'low', label: '', color: '', icon: null };
}

// Price band chip — Last/Min/Max purchase price with a rich tooltip carrying
// GRN number, vendor, date and (optionally) batch/qty. The colour signals
// which band this is at a glance: blue = last paid, green = min (best
// negotiated), red = max (worst paid). Uses the inventory `stocks` history
// fed by the price-history endpoint.
function PriceChip({ kind, entry, samples }) {
  const palette = {
    last: { cls: 'text-blue-700 bg-blue-50 border-blue-200', label: 'Last' },
    min:  { cls: 'text-emerald-700 bg-emerald-50 border-emerald-200', label: 'Min' },
    max:  { cls: 'text-red-700 bg-red-50 border-red-200', label: 'Max' },
  };
  const empty = { cls: 'text-gray-400 bg-gray-50 border-gray-200', label: palette[kind]?.label || 'Last' };
  // Empty / no-history variant — keep the chip visible against every line
  // item so the user always sees the price band; communicates "no inventory
  // history" via the dash and the tooltip copy.
  if (!entry) {
    return (
      <Tooltip><TooltipTrigger asChild>
        <span className={`inline-flex items-center gap-1 text-[9px] font-bold px-1.5 py-0.5 rounded-full border cursor-help ${empty.cls}`}
          data-testid={`price-chip-${kind}-empty`}>
          {empty.label}: —
        </span>
      </TooltipTrigger><TooltipContent side="top" className="max-w-xs bg-slate-900 text-white border border-slate-700 px-3 py-2">
        <p className="text-[11px]">No purchase history found for this medicine in inventory yet.</p>
      </TooltipContent></Tooltip>
    );
  }
  const date = entry.date ? new Date(entry.date).toLocaleDateString('en-IN', { day: '2-digit', month: 'short', year: 'numeric', timeZone: 'Asia/Kolkata' }) : '';
  const p = palette[kind] || palette.last;
  return (
    <Tooltip><TooltipTrigger asChild>
      <span className={`inline-flex items-center gap-1 text-[9px] font-bold px-1.5 py-0.5 rounded-full border cursor-help ${p.cls}`}
        data-testid={`price-chip-${kind}`}>
        {p.label}: ₹{(entry.price || 0).toFixed(2)}
      </span>
    </TooltipTrigger><TooltipContent side="top" className="max-w-xs bg-slate-900 text-white border border-slate-700 px-3 py-2">
      <div className="space-y-1 text-[11px] text-white">
        <p className="font-bold text-white">{p.label} purchased price · ₹{(entry.price || 0).toFixed(2)}</p>
        {entry.grn && <p><span className="text-slate-400">GRN:</span> <span className="text-white">{entry.grn}</span></p>}
        {entry.po && <p><span className="text-slate-400">PO:</span> <span className="text-white">{entry.po}</span></p>}
        {entry.vendor && <p><span className="text-slate-400">Vendor:</span> <span className="text-white">{entry.vendor}</span></p>}
        {date && <p><span className="text-slate-400">Date:</span> <span className="text-white">{date}</span></p>}
        {entry.batch && <p><span className="text-slate-400">Batch:</span> <span className="text-white">{entry.batch}</span></p>}
        {entry.quantity ? <p><span className="text-slate-400">Qty:</span> <span className="text-white">{entry.quantity}</span></p> : null}
        {kind === 'last' && samples > 1 && <p className="text-slate-400 italic mt-0.5">Across {samples} historical receipts</p>}
      </div>
    </TooltipContent></Tooltip>
  );
}

// ==================== VENDORS SCREEN ====================
function VendorsScreen() {
  const [vendors, setVendors] = useState([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState('');
  const [editing, setEditing] = useState(null); // {action: 'new'|'edit', vendor}

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const { data } = await axios.get(`${API_URL}/api/vendors`, {
        params: search.length >= 2 ? { search } : {},
        withCredentials: true,
      });
      setVendors(data || []);
    } catch (e) {
      toast.error('Failed to load vendors');
    }
    setLoading(false);
  }, [search]);

  useEffect(() => {
    const t = setTimeout(load, 250);
    return () => clearTimeout(t);
  }, [load]);

  const handleDelete = async (v) => {
    if (!v.id) return;
    if (!window.confirm(`Delete vendor "${v.name}"? This cannot be undone.`)) return;
    try {
      await axios.delete(`${API_URL}/api/vendors/${v.id}`, { withCredentials: true });
      toast.success('Vendor deleted');
      load();
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Delete failed');
    }
  };

  return (
    <div className="p-6 max-w-7xl mx-auto" data-testid="vendors-screen">
      <div className="bg-white rounded-xl border border-gray-200 p-3 mb-4 flex items-center justify-between gap-3">
        <div className="relative flex-1 max-w-sm">
          <Search className="absolute left-2.5 top-1/2 -translate-y-1/2 h-3.5 w-3.5 text-gray-400" />
          <Input value={search} onChange={(e) => setSearch(e.target.value)}
            placeholder="Search vendors by name..." className="pl-8 h-9 text-xs" data-testid="vendor-search" />
        </div>
        <div className="flex items-center gap-2">
          <Button variant="outline" size="sm" onClick={load} className="h-9 w-9 p-0" title="Refresh"><RefreshCw className="h-3.5 w-3.5" /></Button>
          <Button size="sm" onClick={() => setEditing({ action: 'new', vendor: {} })}
            className="gap-1 bg-blue-700 hover:bg-blue-800 text-xs h-9" data-testid="add-vendor-btn">
            <Plus className="h-3.5 w-3.5" /> Add Vendor
          </Button>
        </div>
      </div>

      <div className="bg-white rounded-xl border border-gray-200 overflow-hidden">
        <div className="grid grid-cols-[1.6fr_1fr_1fr_0.8fr_0.8fr_120px] gap-2 px-4 py-2.5 bg-gray-50 border-b border-gray-200 text-[9px] font-bold text-gray-400 uppercase tracking-wider">
          <div>Vendor Name</div><div>GSTIN / PAN</div><div>Contact</div><div>Source</div><div>Type</div><div className="text-center">Actions</div>
        </div>
        {loading ? <div className="text-center py-16 text-gray-400 text-sm">Loading...</div>
        : vendors.length === 0 ? <div className="text-center py-16 text-gray-400"><Building2 className="h-10 w-10 mx-auto mb-3 opacity-30" /><p className="text-sm">No vendors yet</p></div>
        : (
          <div data-testid="vendor-rows-list">
            {vendors.map((v) => (
              <div key={v.id || v.code} className="grid grid-cols-[1.6fr_1fr_1fr_0.8fr_0.8fr_120px] gap-2 px-4 py-2.5 border-b border-gray-100 hover:bg-blue-50/30 items-center text-xs" data-testid={`vendor-row-${v.code || v.id}`}>
                <div>
                  <p className="font-semibold text-gray-900">{v.name}</p>
                  {v.legal_name && v.legal_name !== v.name && <p className="text-[10px] text-gray-500 truncate">{v.legal_name}</p>}
                  <p className="text-[10px] text-gray-400">{v.code}</p>
                </div>
                <div>
                  <p className="text-gray-700 font-mono text-[11px]">{v.gstin || '-'}</p>
                  {v.pan && <p className="text-[10px] text-gray-500 font-mono">PAN: {v.pan}</p>}
                </div>
                <div>
                  {v.phone && <p className="text-[11px]">{v.phone}</p>}
                  {v.email && <p className="text-[10px] text-gray-500 truncate">{v.email}</p>}
                  {!v.phone && !v.email && <p className="text-gray-400 text-[10px]">-</p>}
                </div>
                <div>
                  <Badge className={`text-[9px] border-0 ${v.source === 'local' ? 'bg-emerald-100 text-emerald-700' : 'bg-gray-100 text-gray-600'}`}>
                    {v.source === 'local' ? 'Local' : 'Catalogue'}
                  </Badge>
                </div>
                <div className="text-[11px] text-gray-700">{v.type || '-'}</div>
                <div className="flex items-center gap-0.5 justify-center">
                  {v.source === 'local' ? (
                    <>
                      <Button variant="ghost" size="sm" onClick={() => setEditing({ action: 'edit', vendor: v })}
                        className="h-7 w-7 p-0 text-blue-600 hover:bg-blue-50" title="Edit"
                        data-testid={`edit-vendor-${v.code}`}>
                        <FileText className="h-3.5 w-3.5" />
                      </Button>
                      <Button variant="ghost" size="sm" onClick={() => handleDelete(v)}
                        className="h-7 w-7 p-0 text-red-500 hover:bg-red-50" title="Delete"
                        data-testid={`delete-vendor-${v.code}`}>
                        <Trash2 className="h-3.5 w-3.5" />
                      </Button>
                    </>
                  ) : (
                    <span className="text-[10px] text-gray-400 italic">Read-only</span>
                  )}
                </div>
              </div>
            ))}
          </div>
        )}
      </div>

      {editing && (
        <VendorFormDialog action={editing.action} vendor={editing.vendor}
          onClose={() => setEditing(null)}
          onSaved={() => { setEditing(null); load(); }} />
      )}
    </div>
  );
}

function VendorFormDialog({ action, vendor, onClose, onSaved }) {
  const [form, setForm] = useState({
    name: vendor.name || '',
    legal_name: vendor.legal_name || '',
    gstin: vendor.gstin || '',
    pan: vendor.pan || '',
    type: vendor.type || '',
    category: vendor.category || '',
    phone: vendor.phone || '',
    email: vendor.email || '',
    address: vendor.address || '',
  });
  const [saving, setSaving] = useState(false);
  const set = (k, v) => setForm(prev => ({ ...prev, [k]: v }));

  const handleSubmit = async () => {
    if (!form.name.trim()) { toast.error('Name is required'); return; }
    setSaving(true);
    try {
      if (action === 'edit') {
        await axios.patch(`${API_URL}/api/vendors/${vendor.id}`, form, { withCredentials: true });
        toast.success('Vendor updated');
      } else {
        await axios.post(`${API_URL}/api/vendors`, form, { withCredentials: true });
        toast.success('Vendor created');
      }
      onSaved();
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Save failed');
    }
    setSaving(false);
  };

  return (
    <DrawerShell
      open
      onClose={onClose}
      eyebrow="Vendor Master"
      title={action === 'edit' ? 'Edit Vendor' : 'Add Vendor'}
      subtitle="GSTIN + PAN power the purchase ledger"
      accent="#1d4ed8"
      icon={<Building2 className="h-5 w-5" />}
      width="lg"
      testId="vendor-form-dialog"
      footer={
        <>
          <Button variant="outline" onClick={onClose}>Cancel</Button>
          <Button onClick={handleSubmit} disabled={saving} className="bg-blue-700 hover:bg-blue-800" data-testid="vf-submit">
            {saving ? 'Saving...' : action === 'edit' ? 'Update' : 'Create'}
          </Button>
        </>
      }
    >
      <div className="p-5 grid grid-cols-2 gap-3">
        <Field label="Trade Name *" value={form.name} onChange={(v) => set('name', v)} testId="vf-name" />
        <Field label="Legal Name" value={form.legal_name} onChange={(v) => set('legal_name', v)} testId="vf-legal-name" />
        <Field label="GSTIN" value={form.gstin} onChange={(v) => set('gstin', v.toUpperCase())} testId="vf-gstin" />
        <Field label="PAN" value={form.pan} onChange={(v) => set('pan', v.toUpperCase())} testId="vf-pan" />
        <Field label="Type (e.g. Distributor)" value={form.type} onChange={(v) => set('type', v)} testId="vf-type" />
        <Field label="Category" value={form.category} onChange={(v) => set('category', v)} testId="vf-category" />
        <Field label="Phone" value={form.phone} onChange={(v) => set('phone', v)} testId="vf-phone" />
        <Field label="Email" value={form.email} onChange={(v) => set('email', v)} testId="vf-email" />
        <div className="col-span-2"><Field label="Address" value={form.address} onChange={(v) => set('address', v)} testId="vf-address" /></div>
      </div>
    </DrawerShell>
  );
}

function Field({ label, value, onChange, testId }) {
  return (
    <div className="space-y-1">
      <Label className="text-[11px] font-semibold text-gray-600">{label}</Label>
      <Input value={value} onChange={(e) => onChange(e.target.value)} className="h-9 text-sm" data-testid={testId} />
    </div>
  );
}

// ==================== MAIN MODULE ====================
export default function PurchaseModule() {
  const [activeTab, setActiveTab] = useState('requisitions');
  const [poToView, setPoToView] = useState(null);
  const [createPOItems, setCreatePOItems] = useState(null);
  const [createPOReqIds, setCreatePOReqIds] = useState([]);
  const { user, setUser } = useAuth();

  // Same posture as the other staff portals — clear the server cookie,
  // wipe local context, then hard-navigate to the unified login so any
  // in-memory PO drafts don't linger on a re-login screen.
  const handleLogout = async () => {
    try { await axios.post(`${API_URL}/api/auth/logout`, {}, { withCredentials: true }); } catch (_) {}
    setUser(null);
    window.location.assign('/login');
  };

  return (
    <div className="min-h-screen bg-gray-50 flex flex-col" data-testid="purchase-module">
      <header className="bg-white border-b border-gray-200 px-6 py-4 flex items-center justify-between flex-shrink-0">
        <div className="flex items-center gap-3">
          <div className="h-10 w-10 rounded-xl bg-gradient-to-br from-blue-700 to-blue-900 flex items-center justify-center shadow-lg"><ShoppingCart className="h-5 w-5 text-white" /></div>
          <div><h1 className="text-xl font-bold text-gray-900 tracking-tight" style={{ fontFamily: 'Manrope' }}>Purchase Management</h1><p className="text-xs text-gray-500">{HOSPITAL.name} &middot; Pharmacy Department</p></div>
        </div>
        <div className="flex items-center gap-2">
          {[
            { key: 'requisitions', label: 'Purchase Requisitions', icon: Package },
            { key: 'orders', label: 'Purchase Orders', icon: FileText },
            { key: 'vendors', label: 'Vendors', icon: Building2 },
          ].map(t => {
            const Icon = t.icon;
            return (<button key={t.key} onClick={() => { setActiveTab(t.key); setPoToView(null); setCreatePOItems(null); }}
              className={`flex items-center gap-2 px-4 py-2 rounded-lg text-sm font-medium transition-all ${activeTab === t.key ? 'bg-blue-700 text-white shadow-md' : 'bg-gray-100 text-gray-600 hover:bg-gray-200'}`} data-testid={`tab-${t.key}`}><Icon className="h-4 w-4" /> {t.label}</button>);
          })}
          {/* Identity + Logout */}
          <div className="flex items-center gap-2 ml-3 pl-3 border-l border-gray-200">
            <div className="text-right hidden sm:block">
              <p className="text-xs font-semibold text-gray-700 leading-tight" data-testid="purchase-username">
                {user?.name || 'purchase01'}
              </p>
              <p className="text-[9px] text-gray-400 uppercase tracking-wider">Purchase</p>
            </div>
            <Button
              variant="outline"
              size="sm"
              onClick={handleLogout}
              className="gap-1 h-9 text-xs"
              data-testid="purchase-logout"
            >
              <LogOut className="h-3.5 w-3.5" /> Logout
            </Button>
          </div>
        </div>
      </header>
      <div className="flex-1 overflow-auto">
        {createPOItems ? <CreatePOScreen items={createPOItems} requisitionIds={createPOReqIds} onBack={() => setCreatePOItems(null)} onCreated={() => { setCreatePOItems(null); setActiveTab('orders'); }} />
        : poToView ? <PODetailScreen po={poToView} onBack={() => setPoToView(null)} />
        : activeTab === 'requisitions' ? <RequisitionsScreen onCreatePO={(items, ids) => { setCreatePOItems(items); setCreatePOReqIds(ids); }} />
        : activeTab === 'vendors' ? <VendorsScreen />
        : <OrdersScreen onViewPO={setPoToView} />}
      </div>
    </div>
  );
}

// ==================== REQUISITIONS SCREEN ====================
function RequisitionsScreen({ onCreatePO }) {
  const [requests, setRequests] = useState([]);
  const [loading, setLoading] = useState(true);
  const [filter, setFilter] = useState('all');
  const [search, setSearch] = useState('');
  const [selected, setSelected] = useState(new Set());
  const [highMoving, setHighMoving] = useState([]);
  const [recentRx, setRecentRx] = useState({});
  const [stockMap, setStockMap] = useState({});
  const [showAddItem, setShowAddItem] = useState(false);
  const [addSearch, setAddSearch] = useState('');
  const [addResults, setAddResults] = useState([]);
  const [addTimeout, setAddTimeout] = useState(null);
  // Auto-aggregate toggle. When on, rows are grouped by medicine_name
  // (case-insensitive) and quantities are summed across the last 7 days.
  // The selection model switches to a `group:<key>` selection so a single
  // checkbox locks an entire group into one PO line item.
  const [aggregate, setAggregate] = useState(false);

  const loadData = useCallback(async () => {
    setLoading(true);
    try {
      const [reqRes, hmRes, rxRes] = await Promise.all([
        axios.get(`${API_URL}/api/purchase-requests`, { withCredentials: true }),
        axios.get(`${API_URL}/api/analytics/high-moving-medicines`, { withCredentials: true }),
        axios.get(`${API_URL}/api/analytics/recent-prescriptions`, { withCredentials: true }),
      ]);
      setRequests(reqRes.data || []);
      setHighMoving(hmRes.data || []);
      setRecentRx(rxRes.data || {});
    } catch (e) { console.error(e); }
    setLoading(false);
  }, []);
  useEffect(() => { loadData(); }, [loadData]);

  useEffect(() => {
    const fetchStocks = async () => {
      const names = [...new Set(requests.map(r => r.medicine_name).filter(Boolean))];
      if (!names.length) return;
      try {
        const { data } = await axios.post(`${API_URL}/api/common/medicines/enrich`, { names }, { withCredentials: true });
        const map = {};
        for (const [name, info] of Object.entries(data)) map[name] = { stock: info.stock, unit: info.subCategory || '' };
        setStockMap(map);
      } catch (e) {}
    };
    if (requests.length) fetchStocks();
  }, [requests]);

  const updateStatus = async (id, status) => {
    try { await axios.patch(`${API_URL}/api/purchase-requests/${id}`, { status }, { withCredentials: true }); setRequests(prev => prev.map(r => r.id === id ? { ...r, status } : r)); toast.success(`Request ${status}`); } catch (e) { toast.error('Failed'); }
  };
  const handleAddSearch = (q) => {
    setAddSearch(q); if (addTimeout) clearTimeout(addTimeout);
    if (!q || q.length < 2) { setAddResults([]); return; }
    const t = setTimeout(async () => { try { const { data } = await axios.get(`${API_URL}/api/common/medicines`, { params: { search: q }, withCredentials: true }); setAddResults(data || []); } catch (e) {} }, 300);
    setAddTimeout(t);
  };
  const addItemFromSearch = (med) => {
    setRequests(prev => [{ id: `manual_${Date.now()}`, medicine_name: med.name, composition: med.composition || '', stock: med.stock, unit: med.unit || '', doctor_name: 'Purchase Dept', quantity: 1, status: 'pending', created_at: new Date().toISOString(), isManual: true }, ...prev]);
    setShowAddItem(false); setAddSearch(''); setAddResults([]); toast.success(`Added ${med.name}`);
  };

  const hmNames = new Set(highMoving.map(h => h.medicine_name?.toUpperCase()));
  const hmMap = {};
  highMoving.forEach(h => { hmMap[h.medicine_name?.toUpperCase()] = h.prescription_count; });

  const filtered = requests.filter(r => {
    if (search && !r.medicine_name.toLowerCase().includes(search.toLowerCase())) return false;
    if (filter === 'pending') return r.status === 'pending';
    if (filter === 'high_moving') return hmNames.has(r.medicine_name?.toUpperCase());
    if (filter === 'no_stock') { const s = stockMap[r.medicine_name]; return s && s.stock <= 0; }
    if (filter === 'low_stock') { const s = stockMap[r.medicine_name]; return s && s.stock > 0 && s.stock <= 10; }
    return true;
  });

  // Aggregate ONLY pending PRs from the last 7 days — already-ordered rows
  // shouldn't merge into a fresh PO. Keys are case-folded medicine names so
  // 'PARACETAMOL' and 'Paracetamol' collapse together. The group exposes
  // every contributing PR id so a single Create-PO click links them all.
  const PRIORITY_RANK = { urgent: 0, high: 1, normal: 2, low: 3 };
  const groupedRows = (() => {
    if (!aggregate) return null;
    const sevenDaysAgo = Date.now() - 7 * 24 * 60 * 60 * 1000;
    const map = new Map();
    for (const r of filtered) {
      if (r.status !== 'pending') continue;
      const ts = r.created_at ? new Date(r.created_at).getTime() : Date.now();
      if (ts < sevenDaysAgo) continue;
      const key = (r.medicine_name || '').trim().toLowerCase();
      if (!key) continue;
      const g = map.get(key) || {
        key,
        medicine_name: r.medicine_name,
        composition: r.composition || '',
        unit: r.unit || '',
        total_qty: 0,
        ids: [],
        sources: new Set(),
        priority: 'low',
        last_mrp: 0,
        priorities: {},
      };
      g.total_qty += Number(r.quantity) || 0;
      if (r.id && !r.id.toString().startsWith('manual_')) g.ids.push(r.id);
      g.sources.add(r.raised_by_role === 'pharmacy' ? 'Pharmacy' : 'Doctor');
      if (r.last_mrp > g.last_mrp) g.last_mrp = r.last_mrp;
      const pr = (r.priority || 'normal').toLowerCase();
      g.priorities[pr] = (g.priorities[pr] || 0) + 1;
      if ((PRIORITY_RANK[pr] ?? 99) < (PRIORITY_RANK[g.priority] ?? 99)) g.priority = pr;
      // Use highest-priority contributing row for composition fallback
      if (!g.composition && r.composition) g.composition = r.composition;
      if (!g.unit && r.unit) g.unit = r.unit;
      map.set(key, g);
    }
    return Array.from(map.values()).sort((a, b) =>
      (PRIORITY_RANK[a.priority] ?? 99) - (PRIORITY_RANK[b.priority] ?? 99)
      || b.total_qty - a.total_qty
    );
  })();

  const toggleSelect = (id) => setSelected(prev => { const n = new Set(prev); n.has(id) ? n.delete(id) : n.add(id); return n; });
  const pendingIds = filtered.filter(r => r.status === 'pending').map(r => r.id);
  const groupKeys = (groupedRows || []).map(g => `group:${g.key}`);
  const toggleAll = () => {
    if (groupedRows) {
      setSelected(prev => prev.size === groupKeys.length ? new Set() : new Set(groupKeys));
    } else {
      setSelected(prev => prev.size === pendingIds.length ? new Set() : new Set(pendingIds));
    }
  };
  // Reset selection any time the user flips between flat and aggregate
  // views — the two modes use different selection keys (PR ids vs group
  // keys) so cross-mode persistence would be confusing.
  useEffect(() => { setSelected(new Set()); }, [aggregate]);

  const noStockCount = requests.filter(r => { const s = stockMap[r.medicine_name]; return s && s.stock <= 0 && r.status === 'pending'; }).length;
  const lowStockCount = requests.filter(r => { const s = stockMap[r.medicine_name]; return s && s.stock > 0 && s.stock <= 10 && r.status === 'pending'; }).length;

  const handleCreatePO = () => {
    if (groupedRows) {
      // Aggregate mode — one PO line per group, qty = sum, contributing PR
      // ids forwarded so the CreatePOScreen can mark all of them ordered
      // in a single batch.
      const groups = groupedRows.filter(g => selected.has(`group:${g.key}`));
      const items = groups.map(g => ({
        medicine_name: g.medicine_name,
        composition: g.composition || '',
        quantity: g.total_qty || 1,
        unit: stockMap[g.medicine_name]?.unit || g.unit || 'Nos',
        unit_price: g.last_mrp || 0,
        current_stock: stockMap[g.medicine_name]?.stock ?? null,
        rx_7d: recentRx[g.medicine_name] || 0,
        aggregated_from: g.ids.length,
      }));
      const linkIds = groups.flatMap(g => g.ids);
      onCreatePO(items, linkIds);
      return;
    }
    const items = filtered.filter(r => selected.has(r.id)).map(r => ({
      medicine_name: r.medicine_name, composition: r.composition || '', quantity: r.quantity || 1,
      unit: stockMap[r.medicine_name]?.unit || r.unit || 'Nos', unit_price: 0,
      current_stock: stockMap[r.medicine_name]?.stock ?? null,
      rx_7d: recentRx[r.medicine_name] || 0,
    }));
    onCreatePO(items, [...selected].filter(id => !id.startsWith('manual_')));
  };

  return (
    <TooltipProvider>
    <div className="p-6 max-w-7xl mx-auto" data-testid="requisitions-screen">
      {/* Stats */}
      <div className="grid grid-cols-6 gap-3 mb-6">
        {[
          { label: 'Total', value: requests.length, icon: Package, color: 'blue', filter: 'all' },
          { label: 'Pending', value: requests.filter(r => r.status === 'pending').length, icon: Clock, color: 'amber', filter: 'pending' },
          { label: 'No Stock', value: noStockCount, icon: ShieldAlert, color: 'red', filter: 'no_stock' },
          { label: 'Low Stock', value: lowStockCount, icon: AlertTriangle, color: 'orange', filter: 'low_stock' },
          { label: 'Ordered', value: requests.filter(r => r.status === 'ordered').length, icon: Truck, color: 'blue', filter: 'all' },
          { label: 'High Moving', value: highMoving.length, icon: TrendingUp, color: 'purple', filter: 'high_moving' },
        ].map((s, i) => {
          const Icon = s.icon;
          const isActive = filter === s.filter && i !== 4;
          return (
            <button key={i} onClick={() => s.filter && setFilter(s.filter)}
              className={`bg-white rounded-xl border p-3 flex items-center gap-3 text-left transition-all ${isActive ? 'border-blue-400 ring-2 ring-blue-100' : 'border-gray-200 hover:border-gray-300'}`} data-testid={`stat-${i}`}>
              <div className={`h-9 w-9 rounded-lg bg-${s.color}-100 flex items-center justify-center flex-shrink-0`}><Icon className={`h-4 w-4 text-${s.color}-700`} /></div>
              <div><p className="text-xl font-bold text-gray-900 leading-tight">{s.value}</p><p className="text-[10px] text-gray-500">{s.label}</p></div>
            </button>
          );
        })}
      </div>

      {/* Toolbar */}
      <div className="bg-white rounded-xl border border-gray-200 p-3 mb-4 flex items-center justify-between gap-3">
        <div className="relative flex-1 max-w-sm">
          <Search className="absolute left-2.5 top-1/2 -translate-y-1/2 h-3.5 w-3.5 text-gray-400" />
          <Input value={search} onChange={(e) => setSearch(e.target.value)} placeholder="Search medicines..." className="pl-8 h-9 text-xs" data-testid="req-search" />
        </div>
        <div className="flex items-center gap-2">
          {/* Aggregate toggle — collapses duplicate raises (last 7 days) into
              single PO lines. Lit when on. */}
          <Tooltip>
            <TooltipTrigger asChild>
              <button onClick={() => setAggregate(a => !a)}
                className={`flex items-center gap-1.5 px-3 h-9 rounded-lg border text-xs font-semibold transition-colors ${
                  aggregate
                    ? 'bg-purple-700 text-white border-purple-700 shadow-sm'
                    : 'bg-white text-gray-700 border-gray-200 hover:border-purple-300 hover:text-purple-700'
                }`}
                data-testid="aggregate-toggle">
                <Layers className="h-3.5 w-3.5" />
                Auto-aggregate
                {aggregate && groupedRows && (
                  <span className="ml-1 inline-flex items-center justify-center px-1.5 rounded bg-white/20 text-[10px]">
                    {groupedRows.length}
                  </span>
                )}
              </button>
            </TooltipTrigger>
            <TooltipContent>
              <p className="text-xs max-w-xs">Group pending PRs (last 7 days) by medicine. One PO line per medicine, summed quantity, all sources combined.</p>
            </TooltipContent>
          </Tooltip>
          <Button variant="outline" size="sm" onClick={loadData} className="h-9 w-9 p-0" title="Refresh"><RefreshCw className="h-3.5 w-3.5" /></Button>
          <Button variant="outline" size="sm" onClick={() => setShowAddItem(true)} className="gap-1 text-xs h-9" data-testid="add-item-btn"><Plus className="h-3.5 w-3.5" /> Add Item</Button>
          <Button size="sm" onClick={handleCreatePO} disabled={selected.size === 0} className="gap-1 bg-blue-700 hover:bg-blue-800 text-xs h-9 px-5" data-testid="create-po-btn">
            <FileText className="h-3.5 w-3.5" /> Create PO ({selected.size})
          </Button>
        </div>
      </div>

      {/* Table */}
      <div className="bg-white rounded-xl border border-gray-200 overflow-hidden">
        {groupedRows ? (
          // Aggregated view — one row per medicine, qty summed across the
          // last 7 days. Selecting a group pulls every contributing PR
          // into a single PO line item via handleCreatePO.
          <>
            <div className="grid grid-cols-[36px_2.2fr_1.5fr_90px_100px_120px_120px_80px] gap-1 px-4 py-2.5 bg-purple-50 border-b border-purple-200 text-[9px] font-bold text-purple-700 uppercase tracking-wider">
              <div><input type="checkbox" checked={selected.size > 0 && selected.size === groupKeys.length} onChange={toggleAll} className="rounded border-gray-300" data-testid="grouped-select-all" /></div>
              <div>Medicine</div><div>Composition</div><div>Total Qty</div><div>Stock</div><div>Sources / Raises</div><div>Priority</div><div className="text-center">Last MRP</div>
            </div>
            {loading ? <div className="text-center py-16 text-gray-400 text-sm">Loading...</div>
            : groupedRows.length === 0 ? <div className="text-center py-16 text-gray-400"><Layers className="h-10 w-10 mx-auto mb-3 opacity-30" /><p className="text-sm">No pending requisitions in the last 7 days to aggregate</p></div>
            : (
              <div data-testid="grouped-rows-list">
                {groupedRows.map((g) => {
                  const stk = stockMap[g.medicine_name];
                  const key = `group:${g.key}`;
                  const isSel = selected.has(key);
                  const sources = Array.from(g.sources);
                  const priCls = g.priority === 'urgent' ? 'bg-red-100 text-red-700 border-red-200 animate-pulse'
                    : g.priority === 'high' ? 'bg-amber-100 text-amber-800 border-amber-200'
                    : g.priority === 'low' ? 'bg-gray-100 text-gray-600 border-gray-200'
                    : 'bg-blue-50 text-blue-700 border-blue-200';
                  return (
                    <div key={key} className={`grid grid-cols-[36px_2.2fr_1.5fr_90px_100px_120px_120px_80px] gap-1 px-4 py-2.5 border-b border-gray-100 hover:bg-purple-50/30 items-center text-sm ${isSel ? 'bg-purple-50' : ''}`} data-testid={`grouped-row-${g.key}`}>
                      <div><input type="checkbox" checked={isSel} onChange={() => toggleSelect(key)} className="rounded border-gray-300" data-testid={`grouped-row-select-${g.key}`} /></div>
                      <div>
                        <p className="font-bold text-gray-900 truncate">{g.medicine_name}</p>
                        <p className="text-[10px] text-gray-500">
                          {g.ids.length} requisition{g.ids.length === 1 ? '' : 's'} • last 7 days
                        </p>
                      </div>
                      <div className="text-[11px] text-blue-600 truncate" title={g.composition}>{g.composition || '-'}</div>
                      <div>
                        <span className="inline-flex items-center text-sm font-bold text-purple-800 bg-purple-100 px-2.5 py-1 rounded-full border border-purple-200">
                          {g.total_qty}{g.unit ? ` ${g.unit}` : ''}
                        </span>
                      </div>
                      <div>
                        {stk ? (
                          <span className={`inline-flex items-center gap-0.5 text-[11px] font-bold px-2 py-0.5 rounded-full border ${
                            stk.stock > 10 ? 'bg-green-50 text-green-700 border-green-200' : stk.stock > 0 ? 'bg-amber-50 text-amber-700 border-amber-200' : 'bg-red-50 text-red-700 border-red-200'
                          }`}><Box className="h-3 w-3" />{stk.stock <= 0 ? 'NIL' : Math.floor(stk.stock)}</span>
                        ) : <span className="text-[11px] text-gray-300">--</span>}
                      </div>
                      <div className="flex flex-wrap gap-1">
                        {sources.map(s => (
                          <span key={s} className={`text-[9px] font-bold px-1.5 py-0 rounded ${
                            s === 'Pharmacy' ? 'bg-emerald-100 text-emerald-700' : 'bg-blue-100 text-blue-700'
                          }`}>{s}</span>
                        ))}
                      </div>
                      <div>
                        <span className={`inline-flex text-[10px] font-bold px-2 py-0.5 rounded-full border ${priCls}`}>
                          {g.priority.toUpperCase()}
                        </span>
                      </div>
                      <div className="text-[11px] text-gray-700 text-center">
                        {g.last_mrp > 0 ? `₹${g.last_mrp.toFixed(2)}` : '-'}
                      </div>
                    </div>
                  );
                })}
              </div>
            )}
          </>
        ) : (
        <>
        <div className="grid grid-cols-[36px_2.2fr_1.5fr_80px_90px_60px_80px_80px_90px] gap-1 px-4 py-2.5 bg-gray-50 border-b border-gray-200 text-[9px] font-bold text-gray-400 uppercase tracking-wider">
          <div><input type="checkbox" checked={selected.size > 0 && selected.size === pendingIds.length} onChange={toggleAll} className="rounded border-gray-300" /></div>
          <div>Medicine</div><div>Composition</div><div>Rx (7 days)</div><div>Stock</div><div>Urgency</div><div>Status</div><div>By</div><div className="text-center">Actions</div>
        </div>
        {loading ? <div className="text-center py-16 text-gray-400 text-sm">Loading...</div>
        : filtered.length === 0 ? <div className="text-center py-16 text-gray-400"><Package className="h-10 w-10 mx-auto mb-3 opacity-30" /><p className="text-sm">No requisitions found</p></div>
        : (
          <div data-testid="req-rows-list">
            {filtered.map((req) => {
              const isHM = hmNames.has(req.medicine_name?.toUpperCase());
              const rxCount = recentRx[req.medicine_name] || 0;
              const stk = stockMap[req.medicine_name];
              const stockVal = stk ? stk.stock : null;
              const urgency = getUrgency(rxCount, stockVal);
              const UrgIcon = urgency.icon;
              return (
                <div key={req.id} className={`grid grid-cols-[36px_2.2fr_1.5fr_80px_90px_60px_80px_80px_90px] gap-1 px-4 py-2.5 border-b border-gray-100 hover:bg-blue-50/30 items-center transition-colors ${selected.has(req.id) ? 'bg-blue-50' : ''}`} data-testid={`req-row-${req.id}`}>
                  <div>{req.status === 'pending' && <input type="checkbox" checked={selected.has(req.id)} onChange={() => toggleSelect(req.id)} className="rounded border-gray-300" />}</div>
                  <div>
                    <p className="font-semibold text-[13px] text-gray-900 leading-tight">{req.medicine_name}</p>
                    <div className="flex items-center gap-1.5 mt-0.5">
                      {isHM && (
                        <Tooltip><TooltipTrigger>
                          <Badge className="bg-purple-100 text-purple-700 text-[8px] border-0 gap-0.5 py-0"><Flame className="h-2.5 w-2.5" />High Moving ({hmMap[req.medicine_name?.toUpperCase()] || ''}x)</Badge>
                        </TooltipTrigger><TooltipContent><p className="text-xs">Prescribed {hmMap[req.medicine_name?.toUpperCase()] || 0} times overall</p></TooltipContent></Tooltip>
                      )}
                      <span className="text-[9px] text-gray-400">{formatDate(req.created_at)}</span>
                    </div>
                  </div>
                  <div className="text-[11px] text-blue-600 truncate" title={req.composition}>{req.composition || '-'}</div>
                  <div>
                    {rxCount > 0 ? (
                      <Tooltip><TooltipTrigger>
                        <span className="inline-flex items-center gap-1 text-[11px] font-bold text-purple-700 bg-purple-50 px-2 py-0.5 rounded-full border border-purple-200"><Activity className="h-3 w-3" />{rxCount}</span>
                      </TooltipTrigger><TooltipContent><p className="text-xs">Prescribed {rxCount} time{rxCount > 1 ? 's' : ''} in the last 7 days</p></TooltipContent></Tooltip>
                    ) : <span className="text-[11px] text-gray-300">0</span>}
                  </div>
                  <div>
                    {stk ? (
                      <Tooltip><TooltipTrigger>
                        <div className="flex flex-col items-start">
                          <span className={`inline-flex items-center gap-0.5 text-[11px] font-bold px-2 py-0.5 rounded-full border ${
                            stk.stock > 50 ? 'bg-green-50 text-green-700 border-green-200' : stk.stock > 10 ? 'bg-green-50 text-green-700 border-green-200' : stk.stock > 0 ? 'bg-amber-50 text-amber-700 border-amber-200' : 'bg-red-50 text-red-700 border-red-200'
                          }`}><Box className="h-3 w-3" />{stk.stock <= 0 ? 'NIL' : Math.floor(stk.stock)}</span>
                          {stk.stock > 0 && stk.stock <= 10 && <span className="text-[8px] text-amber-600 mt-0.5 font-medium">Low Stock</span>}
                        </div>
                      </TooltipTrigger><TooltipContent><p className="text-xs">{stk.stock <= 0 ? 'Out of stock — immediate purchase needed' : stk.stock <= 10 ? `Only ${Math.floor(stk.stock)} ${stk.unit} left` : `${Math.floor(stk.stock)} ${stk.unit} in stock`}</p></TooltipContent></Tooltip>
                    ) : <span className="text-[11px] text-gray-300">--</span>}
                  </div>
                  <div>
                    {UrgIcon && (
                      <Tooltip><TooltipTrigger>
                        <span className={`inline-flex items-center text-[9px] font-bold px-1.5 py-0.5 rounded border ${urgency.color}`}><UrgIcon className="h-3 w-3" /></span>
                      </TooltipTrigger><TooltipContent><p className="text-xs font-semibold">{urgency.label}: {urgency.level === 'critical' ? 'No stock, high demand' : urgency.level === 'high' ? 'Very low stock' : 'Stock running low with active demand'}</p></TooltipContent></Tooltip>
                    )}
                  </div>
                  <div><Badge className={`text-[9px] border-0 ${SC[req.status] || ''}`}>{req.status}</Badge></div>
                  <div className="text-[10px] text-gray-500 truncate">{req.doctor_name}</div>
                  <div className="flex items-center gap-0.5 justify-center">
                    {req.status === 'pending' && (<>
                      <Button variant="ghost" size="sm" onClick={() => updateStatus(req.id, 'ordered')} className="h-7 w-7 p-0 text-blue-600 hover:bg-blue-50" title="Mark Ordered"><Truck className="h-3.5 w-3.5" /></Button>
                      <Button variant="ghost" size="sm" onClick={() => updateStatus(req.id, 'cancelled')} className="h-7 w-7 p-0 text-red-500 hover:bg-red-50" title="Cancel"><X className="h-3.5 w-3.5" /></Button>
                    </>)}
                    {req.status === 'ordered' && <Button variant="ghost" size="sm" onClick={() => updateStatus(req.id, 'received')} className="h-7 w-7 p-0 text-green-600 hover:bg-green-50" title="Mark Received"><Check className="h-3.5 w-3.5" /></Button>}
                  </div>
                </div>
              );
            })}
          </div>
        )}
        </>
        )}
      </div>

      <DrawerShell
        open={showAddItem}
        onClose={() => setShowAddItem(false)}
        eyebrow="Requisition"
        title="Add Medicine to Requisition"
        subtitle="Search master list — pick to add"
        accent="#1d4ed8"
        icon={<Plus className="h-5 w-5" />}
        width="md"
        testId="add-item-dialog"
      >
        <div className="p-5 space-y-3">
          <div className="relative"><Search className="absolute left-2.5 top-1/2 -translate-y-1/2 h-3.5 w-3.5 text-gray-400" /><Input value={addSearch} onChange={(e) => handleAddSearch(e.target.value)} placeholder="Search medicine name or composition..." className="pl-8" data-testid="add-item-search" /></div>
          <ScrollArea className="max-h-[60vh]">
            {addResults.map((med, i) => (
              <button key={i} onClick={() => addItemFromSearch(med)} className="w-full flex items-center justify-between px-3 py-2.5 hover:bg-blue-50 border-b border-gray-100 text-left">
                <div className="min-w-0 flex-1"><p className="font-medium text-sm">{med.name}</p>{med.composition && <p className="text-[10px] text-blue-600 truncate">{med.composition}</p>}</div>
                <span className={`text-[10px] font-semibold px-2 py-0.5 rounded ${med.stock > 10 ? 'bg-green-50 text-green-700' : med.stock > 0 ? 'bg-amber-50 text-amber-700' : 'bg-red-50 text-red-700'}`}>{med.stock <= 0 ? 'No stock' : `${Math.floor(med.stock)} ${med.unit || ''}`}</span>
              </button>
            ))}
          </ScrollArea>
        </div>
      </DrawerShell>
    </div>
    </TooltipProvider>
  );
}


// ==================== CREATE PO SCREEN ====================
function CreatePOScreen({ items: initialItems, requisitionIds, onBack, onCreated }) {
  const [items, setItems] = useState(initialItems.map((it, i) => ({ ...it, id: i, gst_percent: 18, free_qty: 0, suggested_qty: Math.max(10, (it.rx_7d || 0) * 5 - Math.max(0, it.current_stock || 0)) })));
  const [vendors, setVendors] = useState([]);
  const [vendorSearch, setVendorSearch] = useState('');
  const [selectedVendor, setSelectedVendor] = useState(null);
  const [showVendorList, setShowVendorList] = useState(false);
  const [paymentTerms, setPaymentTerms] = useState('Net 30 Days');
  const [deliveryDate, setDeliveryDate] = useState('');
  const [deliveryAddress, setDeliveryAddress] = useState(`${HOSPITAL.name}, ${HOSPITAL.address}, ${HOSPITAL.city}`);
  const [notes, setNotes] = useState('');
  const [submitting, setSubmitting] = useState(false);
  const [lastPurchaseInfo, setLastPurchaseInfo] = useState({});
  // Detailed price history pulled from Inventory `stocks` (last/min/max
  // with full GRN provenance). Drives the per-row price band + tooltips.
  const [priceHistory, setPriceHistory] = useState({});
  // Live UOM catalogue from the backend (sourced from
  // Inventory.inventories.unitOfMeasure + a curated baseline). Loaded once
  // per session; the dropdown falls back to a small static list while
  // pending so the editor never blocks.
  const [uomOptions, setUomOptions] = useState(['Nos', 'Tablet', 'Strips', 'Bottle', 'Vial', 'Box']);
  const vendorRef = useRef(null);

  useEffect(() => {
    const load = async () => {
      try {
        const [vRes, lpRes, phRes, uomRes] = await Promise.all([
          axios.get(`${API_URL}/api/vendors`, { withCredentials: true }),
          axios.post(`${API_URL}/api/analytics/last-purchase-info`, { names: initialItems.map(i => i.medicine_name) }, { withCredentials: true }),
          axios.post(`${API_URL}/api/inventory/price-history`, { names: initialItems.map(i => i.medicine_name) }, { withCredentials: true }),
          axios.get(`${API_URL}/api/purchase/uoms`, { withCredentials: true }),
        ]);
        setVendors(vRes.data || []);
        setLastPurchaseInfo(lpRes.data || {});
        setPriceHistory(phRes.data || {});
        if (uomRes?.data?.uoms?.length) setUomOptions(uomRes.data.uoms);
        // Auto-fill rates from inventory's last purchase price (more
        // accurate than POs because it covers receipts even from manual
        // inwards).
        const lp = lpRes.data || {};
        const ph = phRes.data || {};
        setItems(prev => prev.map(it => {
          if (it.unit_price) return it;
          const phLast = ph[it.medicine_name]?.last?.price;
          if (phLast) return { ...it, unit_price: phLast };
          const info = lp[it.medicine_name];
          if (info) return { ...it, unit_price: info.last_mrp };
          return it;
        }));
      } catch (e) {}
    };
    load();
  }, [initialItems]);

  useEffect(() => {
    const h = (e) => { if (vendorRef.current && !vendorRef.current.contains(e.target)) setShowVendorList(false); };
    document.addEventListener('mousedown', h); return () => document.removeEventListener('mousedown', h);
  }, []);

  const filteredVendors = vendorSearch ? vendors.filter(v => v.name.toLowerCase().includes(vendorSearch.toLowerCase())) : vendors;
  const updateItem = (idx, field, value) => setItems(prev => prev.map((it, i) => i === idx ? { ...it, [field]: value } : it));
  const removeItem = (idx) => setItems(prev => prev.filter((_, i) => i !== idx));
  const subtotal = items.reduce((s, it) => s + (Number(it.quantity) || 0) * (Number(it.unit_price) || 0), 0);
  // GST breakup by percentage
  const gstBreakup = {};
  items.forEach(it => {
    const lineAmt = (Number(it.quantity) || 0) * (Number(it.unit_price) || 0);
    const pct = Number(it.gst_percent) || 0;
    if (!gstBreakup[pct]) gstBreakup[pct] = { taxable: 0, cgst: 0, sgst: 0, igst: 0, total: 0 };
    gstBreakup[pct].taxable += lineAmt;
    gstBreakup[pct].cgst += lineAmt * pct / 200;
    gstBreakup[pct].sgst += lineAmt * pct / 200;
    gstBreakup[pct].total += lineAmt * pct / 100;
  });
  const totalGst = Object.values(gstBreakup).reduce((s, g) => s + g.total, 0);
  const grandTotal = subtotal + totalGst;

  const handleSubmit = async () => {
    if (!selectedVendor) { toast.error('Please select a vendor'); return; }
    if (items.length === 0) { toast.error('Add at least one item'); return; }
    const hasZeroRate = items.some(it => !Number(it.unit_price));
    if (hasZeroRate) { toast.error('Please fill unit price for all items'); return; }
    setSubmitting(true);
    try {
      const { data } = await axios.post(`${API_URL}/api/purchase-orders`, {
        vendor: selectedVendor,
        items: items.map(it => ({ medicine_name: it.medicine_name, composition: it.composition || '', quantity: Number(it.quantity) || 1, free_qty: Number(it.free_qty) || 0, unit: it.unit || 'Nos', unit_price: Number(it.unit_price) || 0, hsn_code: it.hsn_code || '3004', gst_percent: Number(it.gst_percent) || 0 })),
        gst_breakup: gstBreakup, payment_terms: paymentTerms, delivery_date: deliveryDate, delivery_address: deliveryAddress, notes, requisition_ids: requisitionIds, status: 'draft',
      }, { withCredentials: true });
      toast.success(`Purchase Order ${data.po_number} created!`);
      onCreated();
    } catch (e) { toast.error('Failed to create PO'); }
    setSubmitting(false);
  };

  return (
    <TooltipProvider>
    <div className="p-6 max-w-7xl mx-auto" data-testid="create-po-screen">
      <button onClick={onBack} className="flex items-center gap-1 text-sm text-gray-500 hover:text-blue-700 mb-3"><ArrowLeft className="h-4 w-4" /> Back to Requisitions</button>

      {/* Header with summary */}
      <div className="flex items-center justify-between mb-5">
        <div>
          <h2 className="text-2xl font-bold text-gray-900" style={{ fontFamily: 'Manrope' }}>Create Purchase Order</h2>
          <p className="text-sm text-gray-500 mt-0.5">{items.length} item{items.length !== 1 ? 's' : ''} &middot; {HOSPITAL.name}</p>
        </div>
        <div className="flex items-center gap-3">
          <div className="text-right">
            <p className="text-[10px] text-gray-400 uppercase tracking-wider font-bold">Estimated Total</p>
            <p className="text-xl font-bold text-blue-700">{formatCurrency(grandTotal)}</p>
          </div>
          <Button onClick={handleSubmit} disabled={submitting} size="lg" className="bg-blue-700 hover:bg-blue-800 gap-2 px-6" data-testid="submit-po-btn">
            {submitting ? <RefreshCw className="h-4 w-4 animate-spin" /> : <FileText className="h-4 w-4" />} {submitting ? 'Creating...' : 'Create Purchase Order'}
          </Button>
        </div>
      </div>

      <div className="grid grid-cols-12 gap-5">
        {/* Left: Items (8 cols) */}
        <div className="col-span-8 space-y-4">
          <div className="bg-white rounded-xl border border-gray-200 overflow-hidden">
            <div className="px-5 py-3 bg-gray-50 border-b border-gray-200 flex items-center justify-between">
              <h3 className="font-bold text-sm" style={{ fontFamily: 'Manrope' }}>Order Line Items</h3>
              <span className="text-xs text-gray-400">{items.length} item(s) &middot; Subtotal: {formatCurrency(subtotal)}</span>
            </div>

            {items.map((item, idx) => {
              const lp = lastPurchaseInfo[item.medicine_name];
              const ph = priceHistory[item.medicine_name];
              const lineTotal = (Number(item.quantity) || 0) * (Number(item.unit_price) || 0);
              const stockVal = item.current_stock;
              const urgency = getUrgency(item.rx_7d || 0, stockVal);
              const UrgIcon = urgency.icon;
              return (
                <div key={idx} className="border-b border-gray-100 hover:bg-gray-50/40 transition-colors">
                  {/* Main row */}
                  <div className="px-5 py-3 flex items-start gap-4">
                    {/* Item info */}
                    <div className="flex-1 min-w-0">
                      <div className="flex items-center gap-2">
                        <span className="text-[10px] text-gray-400 font-bold w-5">{idx + 1}.</span>
                        <p className="text-sm font-bold text-gray-900">{item.medicine_name}</p>
                        {UrgIcon && <span className={`inline-flex items-center gap-0.5 text-[8px] font-bold px-1.5 py-0 rounded border ${urgency.color}`}><UrgIcon className="h-2.5 w-2.5" />{urgency.label}</span>}
                        {item.aggregated_from > 1 && (
                          <Tooltip><TooltipTrigger>
                            <span className="inline-flex items-center gap-0.5 text-[8px] font-bold text-purple-700 bg-purple-50 border border-purple-200 px-1.5 py-0 rounded">
                              <Layers className="h-2.5 w-2.5" />×{item.aggregated_from}
                            </span>
                          </TooltipTrigger><TooltipContent><p className="text-xs">Aggregated from {item.aggregated_from} requisitions</p></TooltipContent></Tooltip>
                        )}
                      </div>
                      {item.composition && <p className="text-[10px] text-blue-600 ml-5 mt-0.5 truncate">{item.composition}</p>}

                      {/* Context chips */}
                      <div className="flex items-center gap-2 ml-5 mt-1.5 flex-wrap">
                        {item.rx_7d > 0 && (
                          <Tooltip><TooltipTrigger>
                            <span className="inline-flex items-center gap-1 text-[9px] font-semibold text-purple-700 bg-purple-50 px-1.5 py-0.5 rounded-full border border-purple-200"><Activity className="h-2.5 w-2.5" />Rx: {item.rx_7d}/week</span>
                          </TooltipTrigger><TooltipContent><p className="text-xs">Prescribed {item.rx_7d} times in the last 7 days</p></TooltipContent></Tooltip>
                        )}
                        {stockVal !== null && stockVal !== undefined && (
                          <span className={`inline-flex items-center gap-1 text-[9px] font-semibold px-1.5 py-0.5 rounded-full border ${stockVal <= 0 ? 'text-red-700 bg-red-50 border-red-200' : stockVal <= 10 ? 'text-amber-700 bg-amber-50 border-amber-200' : 'text-green-700 bg-green-50 border-green-200'}`}>
                            <Box className="h-2.5 w-2.5" />Stock: {stockVal <= 0 ? 'NIL' : Math.floor(stockVal)}
                          </span>
                        )}
                        {/* Inventory price band — Last/Min/Max with full GRN
                            provenance. Chips are ALWAYS rendered against every
                            line item; if no inventory history exists, an empty
                            "—" variant is shown so the price band stays visible. */}
                        <PriceChip kind="last" entry={ph?.last} samples={ph?.samples} />
                        <PriceChip kind="min" entry={ph?.min} />
                        <PriceChip kind="max" entry={ph?.max} />
                        {!ph?.last && lp && (
                          <span className="inline-flex items-center gap-1 text-[9px] text-gray-500 bg-gray-100 px-1.5 py-0.5 rounded-full border border-gray-200"><Info className="h-2.5 w-2.5" />PO Last: {formatCurrency(lp.last_mrp)} · {lp.last_vendor}</span>
                        )}
                        {item.suggested_qty > 0 && (
                          <Tooltip><TooltipTrigger>
                            <button onClick={() => updateItem(idx, 'quantity', item.suggested_qty)}
                              className="inline-flex items-center gap-0.5 text-[9px] font-semibold text-blue-700 bg-blue-50 px-1.5 py-0.5 rounded-full border border-blue-200 hover:bg-blue-100 cursor-pointer">
                              <Zap className="h-2.5 w-2.5" />Suggest: {item.suggested_qty}
                            </button>
                          </TooltipTrigger><TooltipContent><p className="text-xs">Suggested qty based on demand &amp; current stock. Click to apply.</p></TooltipContent></Tooltip>
                        )}
                      </div>
                    </div>

                    {/* Inputs */}
                    <div className="flex items-center gap-2 flex-shrink-0">
                      <div className="w-16"><label className="text-[8px] text-gray-400 uppercase font-bold block mb-0.5">HSN</label><Input value={item.hsn_code || '3004'} onChange={(e) => updateItem(idx, 'hsn_code', e.target.value)} className="h-8 text-xs" data-testid={`po-item-hsn-${idx}`} /></div>
                      <div className="w-16"><label className="text-[8px] text-gray-400 uppercase font-bold block mb-0.5">Qty</label><Input type="number" value={item.quantity} onChange={(e) => updateItem(idx, 'quantity', e.target.value)} className="h-8 text-xs" data-testid={`po-item-qty-${idx}`} /></div>
                      {/* Free Qty (offer / scheme bonus) — bolt-on column.
                          Goes onto the line as `free_qty` but is NOT billed;
                          subtotal still uses ordered Qty × Rate. Highlighted
                          in emerald so it's visually distinct from the
                          billable Qty input next to it. */}
                      <div className="w-16">
                        <label className="text-[8px] text-emerald-600 uppercase font-bold block mb-0.5" title="Free / scheme quantity. Not billed.">Free</label>
                        <Input
                          type="number"
                          min="0"
                          value={item.free_qty ?? 0}
                          onChange={(e) => updateItem(idx, 'free_qty', Math.max(0, Number(e.target.value) || 0))}
                          className="h-8 text-xs bg-emerald-50/40 border-emerald-200 focus:border-emerald-400"
                          placeholder="0"
                          data-testid={`po-item-free-qty-${idx}`}
                        />
                      </div>
                      <div className="w-20">
                        <label className="text-[8px] text-gray-400 uppercase font-bold block mb-0.5">UOM</label>
                        <Select
                          value={item.unit || 'Nos'}
                          onValueChange={(v) => updateItem(idx, 'unit', v)}
                        >
                          <SelectTrigger className="h-8 text-xs" data-testid={`po-item-uom-${idx}`}>
                            <SelectValue />
                          </SelectTrigger>
                          <SelectContent className="max-h-72">
                            {uomOptions.map((u) => (
                              <SelectItem key={u} value={u}>{u}</SelectItem>
                            ))}
                          </SelectContent>
                        </Select>
                      </div>
                      <div className="w-20"><label className="text-[8px] text-gray-400 uppercase font-bold block mb-0.5">Rate (INR)</label><Input type="number" value={item.unit_price || ''} onChange={(e) => updateItem(idx, 'unit_price', e.target.value)} className="h-8 text-xs" placeholder="0.00" data-testid={`po-item-rate-${idx}`} /></div>
                      <div className="w-16"><label className="text-[8px] text-gray-400 uppercase font-bold block mb-0.5">GST %</label>
                        <Select value={String(item.gst_percent ?? 18)} onValueChange={(v) => updateItem(idx, 'gst_percent', Number(v))}>
                          <SelectTrigger className="h-8 text-xs"><SelectValue /></SelectTrigger>
                          <SelectContent>{[0, 5, 12, 18, 28].map(p => <SelectItem key={p} value={String(p)}>{p}%</SelectItem>)}</SelectContent>
                        </Select>
                      </div>
                      <div className="w-24 text-right"><label className="text-[8px] text-gray-400 uppercase font-bold block mb-0.5">Amount</label><p className="text-sm font-bold text-gray-800 h-8 flex items-center justify-end">{formatCurrency(lineTotal)}</p></div>
                      <div className="w-8 pt-3"><Button variant="ghost" size="sm" onClick={() => removeItem(idx)} className="h-7 w-7 p-0 text-red-400 hover:text-red-600"><Trash2 className="h-3.5 w-3.5" /></Button></div>
                    </div>
                  </div>
                </div>
              );
            })}
            {items.length === 0 && <p className="text-center py-12 text-gray-400 text-sm">No items added</p>}

            {/* Totals with GST breakup */}
            <div className="px-5 py-4 bg-gray-50 border-t border-gray-200">
              <div className="max-w-sm ml-auto space-y-1.5">
                <div className="flex justify-between text-sm"><span className="text-gray-500">Subtotal (Taxable)</span><span className="font-semibold">{formatCurrency(subtotal)}</span></div>
                {/* GST Breakup */}
                {Object.entries(gstBreakup).sort((a,b) => Number(a[0]) - Number(b[0])).map(([pct, g]) => (
                  Number(pct) > 0 && g.taxable > 0 && (
                    <div key={pct} className="border-l-2 border-blue-300 pl-3 py-1 space-y-0.5">
                      <div className="flex justify-between text-xs text-gray-500">
                        <span className="font-semibold text-gray-600">GST @ {pct}%</span>
                        <span className="text-[10px] text-gray-400">on {formatCurrency(g.taxable)}</span>
                      </div>
                      <div className="flex justify-between text-xs">
                        <span className="text-gray-500 pl-2">CGST @ {pct/2}%</span>
                        <span>{formatCurrency(g.cgst)}</span>
                      </div>
                      <div className="flex justify-between text-xs">
                        <span className="text-gray-500 pl-2">SGST @ {pct/2}%</span>
                        <span>{formatCurrency(g.sgst)}</span>
                      </div>
                    </div>
                  )
                ))}
                {totalGst > 0 && (
                  <div className="flex justify-between text-sm pt-1"><span className="text-gray-500">Total GST</span><span className="font-semibold">{formatCurrency(totalGst)}</span></div>
                )}
                <div className="flex justify-between text-lg font-bold pt-2 border-t-2 border-gray-900"><span>Grand Total</span><span className="text-blue-700">{formatCurrency(grandTotal)}</span></div>
              </div>
            </div>
          </div>
        </div>

        {/* Right sidebar (4 cols) */}
        <div className="col-span-4 space-y-4">
          {/* Vendor */}
          <div className="bg-white rounded-xl border border-gray-200 p-5" ref={vendorRef}>
            <h3 className="font-bold text-sm mb-3 flex items-center gap-2" style={{ fontFamily: 'Manrope' }}><Building2 className="h-4 w-4 text-blue-700" /> Vendor / Supplier</h3>
            {selectedVendor ? (
              <div className="p-3 bg-blue-50 rounded-lg border border-blue-200">
                <div className="flex items-start justify-between">
                  <div><p className="font-bold text-sm">{selectedVendor.name}</p><p className="text-[10px] text-gray-500 mt-1">Code: {selectedVendor.code}</p><p className="text-[10px] text-gray-500">GSTIN: {selectedVendor.gstin}</p>{selectedVendor.pan && <p className="text-[10px] text-gray-500">PAN: {selectedVendor.pan}</p>}</div>
                  <button onClick={() => setSelectedVendor(null)} className="text-gray-400 hover:text-red-500"><X className="h-4 w-4" /></button>
                </div>
              </div>
            ) : (
              <div className="relative">
                <Search className="absolute left-2.5 top-1/2 -translate-y-1/2 h-3.5 w-3.5 text-gray-400" />
                <Input value={vendorSearch} onChange={(e) => { setVendorSearch(e.target.value); setShowVendorList(true); }} onFocus={() => setShowVendorList(true)} placeholder="Search vendor..." className="pl-8 h-9 text-xs" data-testid="vendor-search" />
                {showVendorList && (
                  <div className="absolute z-20 mt-1 w-full bg-white border border-gray-200 rounded-lg shadow-lg max-h-48 overflow-auto">
                    {filteredVendors.slice(0, 20).map((v, i) => (
                      <button key={i} onClick={() => { setSelectedVendor(v); setShowVendorList(false); setVendorSearch(''); }} className="w-full px-3 py-2 text-left hover:bg-blue-50 border-b border-gray-50 text-xs">
                        <p className="font-medium">{v.name}</p><p className="text-gray-400">{v.code} &middot; {v.gstin}</p>
                      </button>
                    ))}
                    {filteredVendors.length === 0 && <p className="text-center py-4 text-gray-400 text-xs">No vendors found</p>}
                  </div>
                )}
              </div>
            )}
          </div>

          {/* Order Details */}
          <div className="bg-white rounded-xl border border-gray-200 p-5 space-y-3">
            <h3 className="font-bold text-sm flex items-center gap-2" style={{ fontFamily: 'Manrope' }}><FileText className="h-4 w-4 text-blue-700" /> Order Details</h3>
            <div className="space-y-1.5"><Label className="text-[11px]">Payment Terms</Label>
              <Select value={paymentTerms} onValueChange={setPaymentTerms}><SelectTrigger className="h-9 text-xs"><SelectValue /></SelectTrigger><SelectContent>{['Net 30 Days', 'Net 15 Days', 'Net 60 Days', 'Advance Payment', 'COD', 'Credit'].map(t => <SelectItem key={t} value={t}>{t}</SelectItem>)}</SelectContent></Select></div>
            <div className="space-y-1.5"><Label className="text-[11px]">Expected Delivery Date</Label><Input type="date" value={deliveryDate} onChange={(e) => setDeliveryDate(e.target.value)} className="h-9 text-xs" /></div>
            <div className="space-y-1.5"><Label className="text-[11px]">Delivery Address</Label><textarea value={deliveryAddress} onChange={(e) => setDeliveryAddress(e.target.value)} className="w-full px-3 py-2 border border-gray-200 rounded-lg text-xs resize-none h-16 focus:ring-2 focus:ring-blue-500" /></div>
            <div className="space-y-1.5"><Label className="text-[11px]">Notes / Remarks</Label><textarea value={notes} onChange={(e) => setNotes(e.target.value)} className="w-full px-3 py-2 border border-gray-200 rounded-lg text-xs resize-none h-16 focus:ring-2 focus:ring-blue-500" placeholder="Special instructions..." /></div>
          </div>

          {/* Ship To */}
          <div className="bg-gradient-to-br from-blue-50 to-white rounded-xl border border-blue-100 p-4">
            <p className="text-[9px] font-bold uppercase tracking-widest text-blue-700 mb-1.5">Ship To</p>
            <p className="text-xs font-bold">{HOSPITAL.name}</p>
            <p className="text-[11px] text-gray-600">{HOSPITAL.address}</p>
            <p className="text-[11px] text-gray-600">{HOSPITAL.city}</p>
            <p className="text-[10px] text-gray-500 mt-1">GSTIN: {HOSPITAL.gstin} &middot; Ph: {HOSPITAL.phone}</p>
          </div>
        </div>
      </div>
    </div>
    </TooltipProvider>
  );
}


// ==================== ORDERS + PO DETAIL (unchanged logic, same as before) ====================
function OrdersScreen({ onViewPO }) {
  const [orders, setOrders] = useState([]); const [loading, setLoading] = useState(true); const [filterStatus, setFilterStatus] = useState('all'); const [search, setSearch] = useState('');
  useEffect(() => { (async () => { setLoading(true); try { const { data } = await axios.get(`${API_URL}/api/purchase-orders`, { withCredentials: true }); setOrders(data || []); } catch (e) {} setLoading(false); })(); }, []);
  const filtered = orders.filter(o => { if (search && !o.vendor_name.toLowerCase().includes(search.toLowerCase()) && !o.po_number.toLowerCase().includes(search.toLowerCase())) return false; if (filterStatus !== 'all' && o.status !== filterStatus) return false; return true; });
  return (
    <div className="p-6 max-w-7xl mx-auto" data-testid="orders-screen">
      <div className="grid grid-cols-5 gap-4 mb-6">
        {[{ label: 'All Orders', value: orders.length, key: 'all' }, { label: 'Draft', value: orders.filter(o => o.status === 'draft').length, key: 'draft' }, { label: 'Sent', value: orders.filter(o => o.status === 'sent').length, key: 'sent' }, { label: 'Received', value: orders.filter(o => o.status === 'received').length, key: 'received' }, { label: 'Closed', value: orders.filter(o => o.status === 'closed').length, key: 'closed' }].map((s) => (
          <button key={s.key} onClick={() => setFilterStatus(s.key)} className={`bg-white rounded-xl border p-4 text-left transition-all ${filterStatus === s.key ? 'border-blue-400 ring-2 ring-blue-100' : 'border-gray-200 hover:border-gray-300'}`}>
            <p className="text-2xl font-bold text-gray-900">{s.value}</p><p className="text-xs text-gray-500">{s.label}</p>
          </button>
        ))}
      </div>
      <div className="bg-white rounded-xl border border-gray-200 p-4 mb-4"><div className="relative max-w-md"><Search className="absolute left-2.5 top-1/2 -translate-y-1/2 h-3.5 w-3.5 text-gray-400" /><Input value={search} onChange={(e) => setSearch(e.target.value)} placeholder="Search by PO number or vendor..." className="pl-8 h-9 text-xs" data-testid="po-search" /></div></div>
      <div className="bg-white rounded-xl border border-gray-200 overflow-hidden">
        <div className="grid grid-cols-[1fr_1.5fr_80px_1fr_1fr_1fr_80px] gap-2 px-4 py-3 bg-gray-50 border-b border-gray-200 text-[10px] font-semibold text-gray-500 uppercase tracking-wider"><div>PO Number</div><div>Vendor</div><div>Items</div><div>Total</div><div>Status</div><div>Date</div><div></div></div>
        {loading ? <div className="text-center py-16 text-gray-400 text-sm">Loading...</div> : filtered.length === 0 ? <div className="text-center py-16 text-gray-400"><FileText className="h-10 w-10 mx-auto mb-3 opacity-30" /><p className="text-sm">No purchase orders</p></div> : (
          <div data-testid="po-rows-list">{filtered.map((po) => (
            <button key={po.id} onClick={() => onViewPO(po)} className="w-full grid grid-cols-[1fr_1.5fr_80px_1fr_1fr_1fr_80px] gap-2 px-4 py-3 border-b border-gray-100 hover:bg-blue-50/30 items-center text-left" data-testid={`po-row-${po.id}`}>
              <div className="font-semibold text-sm text-blue-700">{po.po_number}</div><div className="text-sm text-gray-800">{po.vendor_name}</div><div className="text-sm text-gray-600">{po.item_count}</div><div className="text-sm font-semibold">{formatCurrency(po.grand_total)}</div><div><Badge className={`text-[10px] border-0 ${SC[po.status] || ''}`}>{po.status?.replace('_', ' ')}</Badge></div><div className="text-xs text-gray-500">{formatDate(po.created_at)}</div><div className="flex justify-center"><Eye className="h-4 w-4 text-gray-400" /></div>
            </button>
          ))}</div>
        )}
      </div>
    </div>
  );
}

function PODetailScreen({ po: poSummary, onBack }) {
  const [po, setPo] = useState(null); const [loading, setLoading] = useState(true); const [updating, setUpdating] = useState(false); const printRef = useRef(null);
  useEffect(() => { (async () => { try { const { data } = await axios.get(`${API_URL}/api/purchase-orders/${poSummary.id}`, { withCredentials: true }); setPo(data); } catch (e) { toast.error('Failed'); } setLoading(false); })(); }, [poSummary.id]);
  const updateStatus = async (status) => { setUpdating(true); try { await axios.patch(`${API_URL}/api/purchase-orders/${poSummary.id}`, { status }, { withCredentials: true }); setPo(prev => ({ ...prev, status })); toast.success(`Status: ${status}`); } catch (e) { toast.error('Failed'); } setUpdating(false); };
  const handlePrint = () => {
    const c = printRef.current; if (!c) return; const w = window.open('', '_blank');
    w.document.write(`<html><head><title>PO - ${po.po_number}</title><style>*{margin:0;padding:0;box-sizing:border-box}body{font-family:'Segoe UI',Arial,sans-serif;color:#1a1a1a;padding:24px;font-size:11px}@media print{body{padding:10px}}</style></head><body>${c.innerHTML}</body></html>`);
    w.document.close(); setTimeout(() => w.print(), 500);
  };
  if (loading) return <div className="text-center py-20 text-gray-400">Loading...</div>;
  if (!po) return <div className="text-center py-20 text-gray-400">PO not found</div>;
  const qrData = `${API_URL}/purchase?po=${po.po_number}`;
  const totalQty = (po.items || []).reduce((s, it) => s + (Number(it.quantity) || 0), 0);
  const totalFreeQty = (po.items || []).reduce((s, it) => s + (Number(it.free_qty) || 0), 0);
  return (
    <div className="p-6 max-w-5xl mx-auto" data-testid="po-detail-screen">
      <div className="flex items-center justify-between mb-4">
        <button onClick={onBack} className="flex items-center gap-1 text-sm text-gray-500 hover:text-blue-700"><ArrowLeft className="h-4 w-4" /> Back to Orders</button>
        <div className="flex items-center gap-2">
          {po.status === 'draft' && <Button size="sm" onClick={() => updateStatus('sent')} disabled={updating} className="gap-1 bg-blue-700 hover:bg-blue-800 text-xs"><Truck className="h-3.5 w-3.5" /> Send to Vendor</Button>}
          {po.status === 'sent' && <Button size="sm" onClick={() => updateStatus('received')} disabled={updating} className="gap-1 bg-green-700 hover:bg-green-800 text-xs"><Check className="h-3.5 w-3.5" /> Mark Received</Button>}
          {po.status === 'received' && <Button size="sm" onClick={() => updateStatus('closed')} disabled={updating} className="gap-1 text-xs" variant="outline"><CheckCircle className="h-3.5 w-3.5" /> Close PO</Button>}
          <Button size="sm" variant="outline" onClick={handlePrint} className="gap-1 text-xs"><Printer className="h-3.5 w-3.5" /> Print / PDF</Button>
        </div>
      </div>
      <div className="bg-white rounded-xl border border-gray-200 p-4 mb-4"><div className="flex items-center gap-6">
        {['draft', 'sent', 'received', 'closed'].map((s, i) => { const isActive = s === po.status; const isPast = ['draft', 'sent', 'received', 'closed'].indexOf(po.status) > i;
          return (<React.Fragment key={s}>{i > 0 && <div className={`flex-1 h-0.5 ${isPast ? 'bg-blue-700' : 'bg-gray-200'}`} />}<div className={`flex items-center gap-2 ${isActive ? 'text-blue-700 font-bold' : isPast ? 'text-green-600' : 'text-gray-400'}`}><div className={`h-7 w-7 rounded-full flex items-center justify-center text-xs font-bold ${isActive ? 'bg-blue-700 text-white' : isPast ? 'bg-green-100 text-green-700' : 'bg-gray-100'}`}>{isPast ? <Check className="h-3.5 w-3.5" /> : i + 1}</div><span className="text-xs capitalize">{s.replace('_', ' ')}</span></div></React.Fragment>);
        })}
      </div></div>
      {/* Printable PO */}
      <div className="bg-white rounded-xl border border-gray-200 p-8" ref={printRef}>
        <div style={{ display: 'flex', justifyContent: 'space-between', borderBottom: '3px solid #1e3a5f', paddingBottom: 16, marginBottom: 16 }}>
          <div><div style={{ fontSize: 18, fontWeight: 800, color: '#1e3a5f', marginBottom: 2 }}>{HOSPITAL.name}</div><div style={{ fontSize: 10, color: '#666', lineHeight: 1.5 }}>{HOSPITAL.address}<br/>{HOSPITAL.city}<br/>Phone: {HOSPITAL.phone} | Email: {HOSPITAL.email}<br/>GSTIN: {HOSPITAL.gstin}</div><div style={{ fontSize: 13, fontWeight: 800, color: '#1e3a5f', textTransform: 'uppercase', letterSpacing: 2, marginTop: 8 }}>Purchase Order</div></div>
          <div style={{ textAlign: 'right' }}><div style={{ fontSize: 20, fontWeight: 800, color: '#1e3a5f' }}>{po.po_number}</div><div style={{ fontSize: 10, color: '#666', marginTop: 2 }}>Date: {formatDate(po.created_at)}</div><div style={{ fontSize: 10, color: '#666' }}>Status: {po.status?.replace('_', ' ').toUpperCase()}</div>{po.created_by && <div style={{ fontSize: 10, color: '#666' }}>Raised By: {po.created_by}</div>}</div>
        </div>

        {/* PO at-a-glance — 4 KPI tiles */}
        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(4, 1fr)', gap: 8, marginBottom: 14 }}>
          {[
            { l: 'Items', v: (po.items || []).length },
            { l: 'Total Qty', v: totalFreeQty > 0 ? `${totalQty} + ${totalFreeQty} free` : totalQty },
            { l: 'Taxable', v: formatCurrency(po.subtotal || 0) },
            { l: 'Grand Total', v: formatCurrency(po.grand_total || 0), highlight: true },
          ].map((k, i) => (
            <div key={i} style={{ background: k.highlight ? '#1e3a5f' : '#f8f9fa', color: k.highlight ? 'white' : '#1a1a1a', padding: '10px 12px', borderRadius: 6, border: k.highlight ? 'none' : '1px solid #e9ecef' }}>
              <div style={{ fontSize: 9, textTransform: 'uppercase', letterSpacing: 1, opacity: 0.7, marginBottom: 4 }}>{k.l}</div>
              <div style={{ fontSize: 15, fontWeight: 800 }}>{k.v}</div>
            </div>
          ))}
        </div>

        <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 16, marginBottom: 16 }}>
          <div style={{ background: '#f8f9fa', padding: 12, borderRadius: 6, border: '1px solid #e9ecef' }}>
            <div style={{ fontSize: 9, fontWeight: 700, textTransform: 'uppercase', color: '#1e3a5f', letterSpacing: 1, marginBottom: 6 }}>Vendor / Supplier</div>
            <div style={{ fontSize: 13, fontWeight: 700, color: '#333' }}>{po.vendor?.name}</div>
            {po.vendor?.legal_name && po.vendor.legal_name !== po.vendor.name && <div style={{ fontSize: 10, color: '#666' }}>{po.vendor.legal_name}</div>}
            <div style={{ fontSize: 10, color: '#666', marginTop: 4 }}>Code: {po.vendor?.code} &middot; GSTIN: {po.vendor?.gstin}</div>
            {po.vendor?.pan && <div style={{ fontSize: 10, color: '#666' }}>PAN: {po.vendor.pan}</div>}
            {po.vendor?.phone && <div style={{ fontSize: 10, color: '#666' }}>Phone: {po.vendor.phone}</div>}
            {po.vendor?.email && <div style={{ fontSize: 10, color: '#666' }}>Email: {po.vendor.email}</div>}
            {po.vendor?.address && <div style={{ fontSize: 10, color: '#666', marginTop: 2 }}>{po.vendor.address}</div>}
          </div>
          <div style={{ background: '#f8f9fa', padding: 12, borderRadius: 6, border: '1px solid #e9ecef' }}>
            <div style={{ fontSize: 9, fontWeight: 700, textTransform: 'uppercase', color: '#1e3a5f', letterSpacing: 1, marginBottom: 6 }}>Ship To &amp; Terms</div>
            <div style={{ fontSize: 11, color: '#333', whiteSpace: 'pre-line' }}>{po.delivery_address}</div>
            {po.delivery_date && <div style={{ fontSize: 10, color: '#666', marginTop: 6 }}><b>Expected Delivery:</b> {po.delivery_date}</div>}
            <div style={{ fontSize: 10, color: '#666', marginTop: 2 }}><b>Payment Terms:</b> {po.payment_terms}</div>
            <div style={{ fontSize: 10, color: '#666', marginTop: 2 }}><b>Place of Supply:</b> Tamil Nadu (33)</div>
          </div>
        </div>
        <table style={{ width: '100%', borderCollapse: 'collapse' }}><thead><tr>{['#', 'Medicine / Description', 'HSN', 'Qty', 'Free', 'UOM', 'Rate', 'GST%', 'Amount'].map((h, i) => <th key={i} style={{ background: '#1e3a5f', color: 'white', padding: '8px 10px', textAlign: i >= 3 ? 'right' : 'left', fontSize: 9, textTransform: 'uppercase', letterSpacing: 0.5 }}>{h}</th>)}</tr></thead><tbody>
          {po.items?.map((item, i) => {
            const freeQty = Number(item.free_qty) || 0;
            return (
              <tr key={i} style={{ background: i % 2 === 0 ? '#fff' : '#f8f9fa' }}>
                <td style={{ padding: '8px 10px', borderBottom: '1px solid #e9ecef', fontSize: 11 }}>{i + 1}</td>
                <td style={{ padding: '8px 10px', borderBottom: '1px solid #e9ecef', fontSize: 11 }}>
                  <div style={{ fontWeight: 600 }}>{item.medicine_name}</div>
                  {item.composition && <div style={{ fontSize: 9, color: '#2563eb', marginTop: 1 }}>{item.composition}</div>}
                </td>
                <td style={{ padding: '8px 10px', borderBottom: '1px solid #e9ecef', fontSize: 11 }}>{item.hsn_code || '3004'}</td>
                <td style={{ padding: '8px 10px', borderBottom: '1px solid #e9ecef', fontSize: 11, textAlign: 'right' }}>{item.quantity}</td>
                <td style={{ padding: '8px 10px', borderBottom: '1px solid #e9ecef', fontSize: 11, textAlign: 'right', color: freeQty > 0 ? '#047857' : '#9ca3af', fontWeight: freeQty > 0 ? 700 : 400 }}>
                  {freeQty > 0 ? `+${freeQty}` : '—'}
                </td>
                <td style={{ padding: '8px 10px', borderBottom: '1px solid #e9ecef', fontSize: 11, textAlign: 'right' }}>{item.unit}</td>
                <td style={{ padding: '8px 10px', borderBottom: '1px solid #e9ecef', fontSize: 11, textAlign: 'right' }}>{formatCurrency(item.unit_price)}</td>
                <td style={{ padding: '8px 10px', borderBottom: '1px solid #e9ecef', fontSize: 11, textAlign: 'right' }}>{item.gst_percent || 0}%</td>
                <td style={{ padding: '8px 10px', borderBottom: '1px solid #e9ecef', fontSize: 11, textAlign: 'right', fontWeight: 600 }}>{formatCurrency(item.total)}</td>
              </tr>
            );
          })}
          <tr><td colSpan="7"></td><td style={{ padding: '8px 10px', fontSize: 11, textAlign: 'right', fontWeight: 600 }}>Subtotal</td><td style={{ padding: '8px 10px', fontSize: 11, textAlign: 'right', fontWeight: 600 }}>{formatCurrency(po.subtotal)}</td></tr>
          {po.gst_breakup && Object.entries(po.gst_breakup).sort((a,b) => Number(a[0]) - Number(b[0])).map(([pct, g]) => (
            Number(pct) > 0 && g.taxable > 0 && (
              <React.Fragment key={pct}>
                <tr style={{ background: '#f0f4ff' }}><td colSpan="7"></td><td style={{ padding: '6px 10px', fontSize: 10, textAlign: 'right', color: '#1e3a5f', fontWeight: 600 }}>CGST @ {pct/2}%</td><td style={{ padding: '6px 10px', fontSize: 10, textAlign: 'right' }}>{formatCurrency(g.cgst)}</td></tr>
                <tr style={{ background: '#f0f4ff' }}><td colSpan="7"></td><td style={{ padding: '6px 10px', fontSize: 10, textAlign: 'right', color: '#1e3a5f', fontWeight: 600 }}>SGST @ {pct/2}%</td><td style={{ padding: '6px 10px', fontSize: 10, textAlign: 'right' }}>{formatCurrency(g.sgst)}</td></tr>
              </React.Fragment>
            )
          ))}
          {po.gst_amount > 0 && <tr><td colSpan="7"></td><td style={{ padding: '8px 10px', fontSize: 11, textAlign: 'right', fontWeight: 600 }}>Total Tax</td><td style={{ padding: '8px 10px', fontSize: 11, textAlign: 'right', fontWeight: 600 }}>{formatCurrency(po.gst_amount)}</td></tr>}
          <tr style={{ background: '#1e3a5f', color: 'white' }}><td colSpan="7"></td><td style={{ padding: '10px', fontSize: 13, fontWeight: 700 }}>Grand Total (INR)</td><td style={{ padding: '10px', fontSize: 13, fontWeight: 700, textAlign: 'right' }}>{formatCurrency(po.grand_total)}</td></tr>
        </tbody></table>

        {/* Amount in words — required by Indian invoicing conventions */}
        <div style={{ marginTop: 12, padding: '10px 12px', background: '#fef3c7', border: '1px solid #fcd34d', borderRadius: 6 }}>
          <div style={{ fontSize: 9, fontWeight: 700, color: '#78350f', textTransform: 'uppercase', letterSpacing: 1 }}>Amount in Words</div>
          <div style={{ fontSize: 12, fontWeight: 700, color: '#78350f', marginTop: 2 }}>INR {numberToIndianWords(po.grand_total || 0)} Only</div>
        </div>

        {/* Tax Summary — slab-by-slab table for the auditor */}
        {po.gst_breakup && Object.keys(po.gst_breakup).filter(k => Number(k) > 0).length > 0 && (
          <div style={{ marginTop: 14 }}>
            <div style={{ fontSize: 10, fontWeight: 700, color: '#1e3a5f', textTransform: 'uppercase', letterSpacing: 1, marginBottom: 6 }}>Tax Summary</div>
            <table style={{ width: '100%', borderCollapse: 'collapse', border: '1px solid #e9ecef' }}>
              <thead><tr style={{ background: '#f8f9fa' }}>
                {['GST Slab', 'Taxable Value', 'CGST', 'SGST', 'Total Tax'].map((h, i) => (
                  <th key={i} style={{ padding: '6px 10px', fontSize: 9, textAlign: i === 0 ? 'left' : 'right', borderBottom: '1px solid #e9ecef', color: '#1e3a5f', textTransform: 'uppercase', letterSpacing: 0.5 }}>{h}</th>
                ))}
              </tr></thead>
              <tbody>
                {Object.entries(po.gst_breakup).filter(([k, g]) => Number(k) > 0 && g.taxable > 0).sort((a, b) => Number(a[0]) - Number(b[0])).map(([pct, g]) => (
                  <tr key={pct}>
                    <td style={{ padding: '6px 10px', fontSize: 11, fontWeight: 600 }}>{pct}%</td>
                    <td style={{ padding: '6px 10px', fontSize: 11, textAlign: 'right' }}>{formatCurrency(g.taxable)}</td>
                    <td style={{ padding: '6px 10px', fontSize: 11, textAlign: 'right' }}>{formatCurrency(g.cgst)}</td>
                    <td style={{ padding: '6px 10px', fontSize: 11, textAlign: 'right' }}>{formatCurrency(g.sgst)}</td>
                    <td style={{ padding: '6px 10px', fontSize: 11, textAlign: 'right', fontWeight: 600 }}>{formatCurrency(g.total)}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}

        {po.notes && <div style={{ marginTop: 16, padding: 10, background: '#fffbeb', border: '1px solid #fde68a', borderRadius: 6 }}><div style={{ fontSize: 9, fontWeight: 700, color: '#92400e', marginBottom: 2, textTransform: 'uppercase' }}>Notes / Remarks</div><div style={{ fontSize: 11, color: '#78350f' }}>{po.notes}</div></div>}

        {/* Standard PO terms & conditions — keeps the print self-contained */}
        <div style={{ marginTop: 16, padding: 12, background: '#f8f9fa', border: '1px solid #e9ecef', borderRadius: 6 }}>
          <div style={{ fontSize: 9, fontWeight: 700, color: '#1e3a5f', textTransform: 'uppercase', letterSpacing: 1, marginBottom: 6 }}>Terms &amp; Conditions</div>
          <ol style={{ fontSize: 10, color: '#444', paddingLeft: 16, lineHeight: 1.6 }}>
            <li>All goods must be supplied as per the specifications and quantity stated above.</li>
            <li>Tax invoice with HSN, batch number, expiry date and GSTIN must accompany every consignment.</li>
            <li>Goods nearing expiry (less than 6 months shelf life) will not be accepted unless pre-approved.</li>
            <li>Cold-chain items must be transported in validated thermal containers; data-loggers are mandatory.</li>
            <li>Defective / damaged goods will be returned at vendor's cost within 7 days of receipt.</li>
            <li>Payment will be released as per the agreed payment terms after successful inward inspection.</li>
            <li>This Purchase Order is governed by the laws of Tamil Nadu, India and is subject to Tenkasi jurisdiction.</li>
          </ol>
        </div>

        <div style={{ marginTop: 30, display: 'grid', gridTemplateColumns: '1fr 1fr 1fr', gap: 40 }}>
          <div><div style={{ borderTop: '1px solid #333', marginTop: 50, paddingTop: 4, fontSize: 10, color: '#666' }}>Prepared By {po.created_by ? `(${po.created_by})` : ''}</div></div>
          <div><div style={{ borderTop: '1px solid #333', marginTop: 50, paddingTop: 4, fontSize: 10, color: '#666' }}>Authorized Signatory</div></div>
          <div style={{ textAlign: 'center' }}><div style={{ fontSize: 9, fontWeight: 700, color: '#1e3a5f', marginBottom: 4 }}>Scan for PO Details</div><img src={`https://api.qrserver.com/v1/create-qr-code/?size=100x100&data=${encodeURIComponent(qrData)}`} alt="QR" style={{ width: 100, height: 100, margin: '0 auto' }} /><div style={{ fontSize: 8, color: '#999', marginTop: 2 }}>{po.po_number}</div></div>
        </div>

        <div style={{ marginTop: 16, paddingTop: 8, borderTop: '1px solid #e9ecef', fontSize: 9, color: '#999', textAlign: 'center' }}>
          This is a system-generated Purchase Order. Computer-generated documents are valid without a physical signature.
        </div>
      </div>
    </div>
  );
}

// Lightweight INR amount-to-words converter — handles up to 99,99,99,999.99
// using the Indian numbering system (lakh / crore). No external dep needed
// for a single output line.
function numberToIndianWords(value) {
  const num = Math.floor(Math.abs(Number(value) || 0));
  const decimals = Math.round((Math.abs(Number(value) || 0) - num) * 100);
  const ones = ['', 'One', 'Two', 'Three', 'Four', 'Five', 'Six', 'Seven', 'Eight', 'Nine', 'Ten',
    'Eleven', 'Twelve', 'Thirteen', 'Fourteen', 'Fifteen', 'Sixteen', 'Seventeen', 'Eighteen', 'Nineteen'];
  const tens = ['', '', 'Twenty', 'Thirty', 'Forty', 'Fifty', 'Sixty', 'Seventy', 'Eighty', 'Ninety'];
  const twoDigits = (n) => n < 20 ? ones[n] : `${tens[Math.floor(n / 10)]}${n % 10 ? ' ' + ones[n % 10] : ''}`;
  const threeDigits = (n) => {
    const h = Math.floor(n / 100);
    const r = n % 100;
    return [h ? `${ones[h]} Hundred` : '', r ? twoDigits(r) : ''].filter(Boolean).join(' ');
  };
  if (num === 0) return 'Zero' + (decimals ? ` and ${twoDigits(decimals)} Paise` : '');
  const crore = Math.floor(num / 10000000);
  const lakh = Math.floor((num % 10000000) / 100000);
  const thousand = Math.floor((num % 100000) / 1000);
  const hundred = num % 1000;
  const parts = [
    crore ? `${twoDigits(crore)} Crore` : '',
    lakh ? `${twoDigits(lakh)} Lakh` : '',
    thousand ? `${twoDigits(thousand)} Thousand` : '',
    hundred ? threeDigits(hundred) : '',
  ].filter(Boolean);
  let words = parts.join(' ');
  if (decimals) words += ` and ${twoDigits(decimals)} Paise`;
  return words;
}
